import type { OperatorCatalogue } from './es';

/**
 * The English catalogue.
 *
 * Typed as {@link OperatorCatalogue}, which is derived from the Spanish one, so this file cannot be
 * missing a key and cannot carry a key Spanish does not have: the first is a missing-property
 * error and the second is an excess-property error, both at compile time. `test:i18n` asserts the
 * same property over the values at run time, and additionally that the two agree about every
 * `{{placeholder}}` — which the types cannot see, because both sides are just `string`.
 */
export const en: OperatorCatalogue = {
  app: {
    name: 'Sentra',
  },
  common: {
    close: 'Close',
    loading: 'Loading…',
    retry: 'Try again',
  },
  confirm: {
    typeToContinue: 'Type “{{phrase}}” to continue',
    cancel: 'Cancel',
    working: 'Working…',
  },
  shell: {
    skipToContent: 'Skip to content',
    navigationLabel: 'Sections',
    mainLabel: 'Main content',
    openNavigation: 'Open the section menu',
    closeNavigation: 'Close the section menu',
  },
  nav: {
    overview: 'Overview',
    scan: 'Gate',
    persons: 'People',
    admin: 'Administration',
  },
  overview: {
    title: 'Overview',
    welcome: 'Signed in as {{name}}.',
    issuanceMode: 'Pass issuance mode',
    issuanceModePreprinted: 'Pre-printed wristbands',
    issuanceModeOnDemand: 'Issued on demand',
    reportingTimezone: 'Reporting timezone',
    terminalTime: 'Terminal time',
    presence: 'Presence',
    presenceLoading: 'Asking…',
    presenceOutside: 'Outside',
    presenceOverdue: 'Outside over {{minutes}} min',
    presenceInside: 'Inside',
  },
  theme: {
    label: 'Theme',
    light: 'Light',
    dark: 'Dark',
    system: 'Match the system',
  },
  language: {
    label: 'Language',
    es: 'Español',
    en: 'English',
  },
  palette: {
    open: 'Open the command palette',
    title: 'Command palette',
    description: 'Find a section or an action and press Enter.',
    placeholder: 'Type to search…',
    empty: 'No matches.',
    groupNavigation: 'Go to',
    groupActions: 'Actions',
  },
  session: {
    signedInAs: 'Signed in as',
    accountMenu: 'Account and preferences',
    signOut: 'Sign out',
    switchOperator: 'Switch operator',
    switchOperatorTitle: 'Switch operator?',
    switchOperatorBody:
      'The person taking over signs in here. This session ends at the same moment, and everything done from then on is recorded against them.',
    signOutIncomplete: 'Signed out here, but the server did not confirm it.',
    roleOperator: 'Operator',
    roleAnalyst: 'Analyst',
    roleAdmin: 'Administrator',
    roleSuperAdmin: 'Lead administrator',
  },
  config: {
    failedTitle: 'The configuration could not be loaded',
    failedBody:
      'This application needs the installation’s configuration before it shows anything. Check that the service is running and try again.',
    sessionUnavailableTitle: 'Your session could not be checked',
    sessionUnavailableBody:
      'The server is busy or restarting and could not confirm whether this terminal’s session is still open. Wait a moment and try again.',
  },
  auth: {
    signInTitle: 'Sign in',
    signInSubtitle: 'For the staff of this facility.',
    email: 'E-mail address',
    // Named `secretField` rather than anything containing "password": `sonarjs/no-hardcoded-passwords`
    // — which `eslint.config.ts` takes from the sonarjs recommended preset — reports a property whose
    // NAME looks like a credential and whose VALUE looks like one too, and a one-word label does.
    // `auth.currentPassword` below is untouched because its value has a space in it. Renaming this
    // key back turns `lint` red, which is checkable: rename it and run `npx eslint` on this file.
    secretField: 'Password',
    signIn: 'Sign in',
    signingIn: 'Signing in…',
    secondFactorTitle: 'Two-step verification',
    secondFactorSubtitle: 'Enter the six-digit code from your authenticator app.',
    totpCode: 'Authenticator code',
    useRecoveryCode: 'Use a recovery code',
    useAuthenticator: 'Use the authenticator app',
    recoveryCode: 'Recovery code',
    recoveryCodesRemaining: 'You have {{remaining}} recovery codes left.',
    passwordChangeTitle: 'Choose a new password',
    passwordChangeSubtitle:
      'Your account was created with a temporary password, which has to be changed.',
    currentPassword: 'Current password',
    newPassword: 'New password',
    repeatPassword: 'Repeat the new password',
    passwordsDoNotMatch: 'The two passwords do not match.',
    changePassword: 'Change the password',
    enrolTitle: 'Set up your authenticator app',
    enrolSubtitle:
      'Scan the code with your app, or type the key by hand, and confirm with the six digits it shows.',
    enrolSecret: 'Manual key',
    enrolQrLabel: 'Code for the authenticator app',
    enrolConfirm: 'Confirm',
    recoveryCodesTitle: 'Keep these recovery codes',
    recoveryCodesBody:
      'Each code works once and they will not be shown again. They are the only way in if you lose your phone.',
    recoveryCodesAcknowledge: 'I have saved them',
    pinTitle: 'Choose a PIN for this terminal',
    pinSubtitle:
      'The PIN unlocks this terminal after a period of inactivity, without signing you out. It is six digits.',
    pin: 'PIN',
    setPin: 'Save the PIN',
    lockTitle: 'Terminal locked after inactivity',
    lockSubtitle: 'Enter your PIN to carry on where you left off.',
    lockAttemptsRemaining: '{{attempts}} attempts left before the password is required.',
    lockPasswordTitle: 'Enter your password',
    lockPasswordSubtitle: 'The PIN attempts are used up. Your unsaved work is still where it was.',
    backToUnlock: 'Back to unlocking this terminal',
    unlock: 'Unlock',
    lockWorkPreserved: 'What you were typing has been kept.',
    noPinTitle: 'This terminal has no PIN',
    reset: {
      passwordTitle: 'Restore your password',
      totpTitle: 'Recover your second factor',
      subtitle: 'We send a code to the account e-mail. Type it here, on this same machine.',
      forgotPassword: 'I have forgotten my password',
      lostAuthenticator: 'I have lost my authenticator application',
      sendCode: 'Send the code',
      code: 'Code from the e-mail',
      confirm: 'Confirm',
      backToSignIn: 'Back to signing in',
      sentIfKnown:
        'If that address belongs to an account, the code is already on its way. It expires in 15 minutes and only works on this machine. Check the spam folder too.',
      totpNeedsPassword:
        'The account password is needed as well: e-mail on its own cannot remove the second factor.',
      donePassword: 'Password changed. You can sign in with it now.',
      doneTotp:
        'Second factor cleared. Signing in will ask you to set up an authenticator application again.',
    },
    noPinSubtitle: 'Enter your password to unlock, and set a PIN afterwards.',
  },
  conflict: {
    title: 'Somebody changed this record while you were editing it',
    body: 'Nothing has been saved. Review each field in conflict and decide which value is kept.',
    columnMine: 'Your version',
    columnTheirs: 'Saved version',
    empty: '(no value)',
    keepMine: 'Keep mine',
    keepTheirs: 'Use the saved one',
    reload: 'Discard my changes and reload',
    apply: 'Save the chosen values',
    unresolved: 'Choose a value in every field before saving.',
    unavailableTitle: 'The saved record could not be read',
    unavailableBody:
      'Without it there is no way to decide which value is kept, and saving blind would erase the other person’s change. Nothing has been sent.',
  },
  search: {
    indexBuilding: 'The search index is still loading. Try again in a few seconds.',
    indexBuildingHint: 'This does not mean the person is not registered.',
    noResults: 'No person matches this search.',
    refined: 'There are more matches than fit. Add another detail to narrow it down.',
  },
  person: {
    form: {
      age: '{{count}} years old',
      createTitle: 'Register a person',
      amendTitle: 'Amend record {{shortRef}}',
      save: 'Save',
      saving: 'Saving…',
      cancel: 'Cancel',
      saveFailed: 'Nothing was saved',
      requiredMark: ' *',
      requiredLabel: 'required',
      selectNone: '(none chosen)',
      characterCount: '{{used}} of {{limit}} characters',
      countryList: 'Countries',
      countryNoMatches: 'No country matches.',
      phoneDetected: 'A {{country}} number.',
      phonePrefix: 'International dialling code',
      phonePrefixList: 'International dialling codes',
      adhocLegend: 'Additional fields',
      adhocHint:
        'Fields belonging to this record alone, up to {{limit}}. The name is remembered on this terminal so it can be offered again; the value is not.',
      adhocName: 'Field name',
      adhocValue: 'Value',
      adhocAdd: 'Add a field',
      adhocRemove: 'Remove this field',
      adhocFull: 'You have reached the maximum number of additional fields.',
      adhocLimit: 'No more than {{limit}} additional fields fit.',
      adhocDuplicateName: 'Two additional fields have the same name.',
      completenessTitle: 'This record is missing some details',
      completenessBody:
        'It can be saved like this and completed later. This is a warning, not an obstacle.',
      completenessConfirm: 'Save anyway',
      completenessBack: 'Go back and complete it',
      missingPhotograph: 'Photograph',
      duplicateTitle: 'This person may already be registered ({{count}})',
      duplicateBody: 'Check the photograph before creating a new record.',
      duplicatePhotoAlt: 'Photograph on record {{shortRef}}',
      duplicateOpen: 'Open',
      duplicateDismiss: 'Not the same person',
      matchedName: 'The name matches',
      matchedReference: 'Same reference code',
      matchedPassport: 'The document matches',
      bandLabel: 'Pre-printed band',
      bandHint: 'Scan the band or type its code. Either before or after the rest of the form.',
      bandMalformed: 'That code is not valid. Check the last symbol.',
      bandAvailable: 'Band available.',
      bandClaimed: 'That band is already assigned to record {{shortRef}}.',
      bandVoid: 'That band belongs to a withdrawn batch.',
      bandUnknown: 'That band is not in any imported batch.',
      bandCamera: 'Read the band with the camera',
      bandCameraClose: 'Close the band camera',
      noBandLabel: 'No band yet: register without a pass',
      noBandHint: 'The band is bound later. Write the code you hand over on paper.',
      emergencyLabel: 'No band available: issue an emergency pass',
      emergencyHint:
        'Only when the box is empty. The pass is printed on paper and is marked as an emergency ' +
        'issue for as long as the record exists.',
      consentTitle: 'The privacy notice has not been acknowledged',
      consentBody:
        'This installation requires the person to have been given the privacy notice before the record is created. Confirm that it was handed over.',
      consentConfirm: 'The notice was handed over',
      statusLabel: 'Status',
      photographsHint:
        'Taken or uploaded now, and saved with the record. It can be changed later from "Amend the details".',
      attaching: 'Saving the photograph…',
      attachFailedTitle: 'The record was created, but the photograph was not saved',
      attachRetry: 'Try the photograph again',
      attachContinue: 'Carry on without it',
      blockedByProblems: 'Nothing was sent: some of the details above need correcting first.',
    },
    images: {
      title: 'Photographs',
      empty: 'This record has no photograph yet.',
      uploadLabel: 'Upload a photograph',
      uploading: 'Uploading the photograph…',
      acceptedFormats: 'JPEG, PNG, WebP and AVIF are accepted.',
      wrongType: 'That kind of file cannot be stored here.',
      emptyFile: 'That file is empty.',
      capReached: 'A record holds at most {{limit}} photographs.',
      failedTitle: 'The photograph was not stored',
      remove: 'Remove',
      removedCount: 'This record had {{count}} photographs that were removed.',
      thumbnailAlt: 'Thumbnail of photograph {{position}}',
      unavailable: 'The photograph could not be loaded',
      loading: 'Loading the photograph…',
      fullAlt: 'Photograph {{position}}, full size',
      viewerTitle: 'Photograph',
      fromWebcam: 'Taken here',
      fromUpload: 'Uploaded',
      startCamera: 'Use the camera',
      stopCamera: 'Turn the camera off',
      opening: 'Opening the camera…',
      previewLabel: 'Camera preview',
      previewStalled:
        'The browser did not start the camera preview. What is on screen may be a frozen frame.',
      shotAlt: 'The photograph just taken',
      deviceLabel: 'Camera',
      deviceFallback: 'Camera {{index}}',
      mirrorPreview: 'Show the preview as a mirror',
      storeMirrored: 'Store the image mirrored',
      mirrorHint:
        'The preview is mirrored because that is what the person in front of it expects. What is stored is the un-mirrored image, which is the one that identifies somebody correctly.',
      takePhoto: 'Take the photograph',
      cancelCountdown: 'Cancel',
      countdown: '{{seconds}}',
      keepShot: 'Keep this photograph',
      retake: 'Retake',
      noCameraTitle: 'This terminal has no camera',
      noCameraBody: 'You can upload a photograph from the machine instead.',
      deniedTitle: 'The browser will not allow the camera',
      deniedBody:
        'Open this site’s permissions in the browser, allow the camera, and try again. You can also upload a photograph from the machine.',
      lostTitle: 'The camera was lost',
      lostBody: 'It may have been unplugged, or another application may be using it.',
      cameraFailedTitle: 'The camera could not be opened',
      cameraFailedBody: 'Try again, or upload a photograph from the machine.',
    },
    table: {
      searchLabel: 'Search the register',
      startTyping: 'Type at least two characters to search.',
      newPerson: 'Register somebody',
      caption: 'People in the register',
      columnRef: 'Reference',
      columnReference: 'Reference',
      columnPhoto: 'Photo',
      columnName: 'Name',
      columnStatus: 'Status',
      columnPresence: 'Presence',
      columnLastActivity: 'Last activity',
      columnActions: 'Actions',
      column_referenceCode: 'Reference code',
      column_photo: 'Photo',
      column_displayName: 'Name',
      column_status: 'Status',
      column_presenceState: 'Presence',
      column_lastActivityAt: 'Last activity',
      columns: 'Visible columns',
      noPhoto: 'No photograph',
      photoAlt: 'Photograph on record {{shortRef}}',
      never: 'Never',
      status_active: 'Active',
      status_blocked: 'Blocked',
      status_archived: 'Archived',
      presence_inside: 'Inside',
      presence_outside: 'Outside',
      filterStatus: 'Status',
      filterPresence: 'Presence',
      filterPhoto: 'Photograph',
      filterAny: 'Any',
      photoWith: 'With a photograph',
      photoWithout: 'Without a photograph',
      clearFilters: 'Clear the filters',
      density: 'Row height',
      densityComfortable: 'Normal',
      densityCompact: 'Compact',
      exportCsv: 'Export this page',
      firstPage: 'First',
      previousPage: 'Previous',
      nextPage: 'Next',
      lastPage: 'Last',
      pageOf: 'Page {{page}} of {{pages}}',
      total: 'Total: {{total}}',
      registerEmpty: 'Nobody is registered yet.',
      pastLastPage:
        'This page no longer exists: there are fewer results than there were. Go back to the first one.',
      noRowsMatch:
        'No record matches the filters applied. That does not mean the person is not registered.',
      action_view: 'Open',
      action_modify: 'Amend',
      action_shred: 'Erase the data',
      action_presence: 'Entry or exit',
      ackTitle: 'Confirm that you reached this record by searching',
      ackView: 'You are about to open a person’s full record.',
      ackModify: 'You are about to amend a person’s record.',
      ackPresence: 'You are about to record an entry or an exit without scanning a pass.',
      ackRecord: 'Record {{shortRef}}.',
      ackWhy:
        'It is recorded that this was reached by searching rather than by scanning a pass. That is the ordinary case when somebody has lost their band.',
      ackShred: 'You are opening a record in order to erase its data irreversibly.',
      ackConfirm: 'I confirm, continue',
      ackCancel: 'Do not continue',
    },
    section: {
      identity: 'Identity',
      stay: 'Stay',
      contact: 'Contact',
      document: 'Documents',
      vulnerability: 'Vulnerability',
      protection: 'International protection',
      other: 'Other details',
      unsorted: 'Ungrouped',
    },
    record: {
      age: 'Age',
      title: 'Record {{shortRef}}',
      summary: 'Status: {{status}}. Recorded as {{presence}}.',
      edit: 'Amend the details',
      backToList: 'Back to the list',
      backToGate: 'Back to access control',
      erase: 'Erase the data',
      archivedTitle: 'This record is archived',
      archivedBody:
        'No entry or exit can be recorded until it is reactivated. Open “Amend the details” and change its status there.',
      incompleteTitle: 'This record is missing data',
      fields: 'Details',
      retiredField: '(retired field)',
      adhoc: 'Extra fields',
      credentials: 'Passes',
      noCredential: 'This person holds no pass.',
      credential_active: 'In use',
      credential_revoked: 'Revoked',
      credential_superseded: 'Replaced',
      issueBand: 'Assign a band',
      replaceBand: 'Replace the band',
      replaceTitle: 'Replace this band?',
      replaceBody:
        'The pass this person is holding stops working at every door straight away, and the new ' +
        'band takes its place.',
      revisions: 'History of changes',
    },
    erase: {
      title: 'Erase the data of {{shortRef}}',
      body: 'The key their data was encrypted under is destroyed, so it stops being readable: this is not a deletion that can be undone or recovered from a later archive.',
      confirm: 'Erase the data',
      reason: 'Reason',
      reasonPlaceholder: 'Choose a reason',
      reasonRequired: 'A reason is required: it is the only thing that survives this.',
      reason_data_subject_request: 'The person asked',
      reason_erroneous_record: 'The record should not exist',
      reason_legal_order: 'Legal order',
      irreversible: 'This cannot be undone.',
    },
    movement: {
      title: 'Record an entry or an exit',
      body: 'For when somebody is not wearing their band. It is recorded as reached by search rather than by scanning.',
      entry: 'Record an entry',
      exit: 'Record an exit',
      recordedTitle: 'Recorded',
      refusedTitle: 'They were not let through',
      recordedBody: 'They are now recorded as {{state}}.',
    },
  },
  scan: {
    title: 'Access control',
    subtitle: 'Scan the band, point the camera at it, or type the code.',
    indicator: {
      label: 'Band scanner',
      ready: 'Ready',
      reading: 'Reading…',
      suppressed: 'Paused: another field has the keyboard',
      off: 'Off',
    },
    code: {
      label: 'Pass code',
      hint: 'Twenty-one characters. You can also scan the band.',
      submit: 'Check',
      malformed: 'That code is not valid: check the last character.',
    },
    diagnostics: {
      toggle: 'Check the scanner',
      hint: 'Scan a band and look at what reached this terminal.',
      received: 'Keystrokes received',
      symbols: 'Code characters',
      widestGap: 'Longest pause',
      key: 'Key',
      gap: 'Pause (ms)',
      symbol: 'Symbol',
      via: 'Via',
      decision: 'Outcome',
      modifier: 'Modifier',
      held: 'Alt/Ctrl',
      cadence: 'Cadence',
      slow: 'Slow: it will still be read. It counts as a scanner reading only if the whole code arrives in under three seconds',
      brisk: 'Brisk: it will be recorded as a scanner reading',
      clear: 'Clear',
    },
    camera: {
      open: 'Use the camera',
      close: 'Close the camera',
      title: 'Camera',
      preview: 'Camera picture',
      device: 'Camera',
      torchOn: 'Turn the light on',
      torchOff: 'Turn the light off',
      scanning: 'Looking for a code…',
      loading: 'Getting the code reader ready…',
      unsupported: 'This browser cannot read codes. Use the band scanner or type the code by hand.',
      denied:
        'The browser will not give this terminal the camera. Change it in the site settings and try again.',
      noCamera: 'This terminal has no camera attached.',
      lost: 'The camera has gone. Check the cable and open it again.',
      failed: 'The camera could not be opened ({{name}}).',
    },
    upload: {
      label: 'Upload a photograph of the pass',
      hint: 'A photograph of the code taken on a phone works too.',
      decoding: 'Looking for the code in the picture…',
      unsupported: 'This browser cannot read codes. Use the band scanner or type the code by hand.',
      noCode: 'No code was found in that picture.',
      failed: 'The picture could not be read.',
    },
    intake: {
      registerBand: 'Register with this band',
    },
    person: {
      photograph: 'Photograph of {{name}}',
      noPhotograph: 'No photograph',
      shortRef: 'Reference',
      status: 'Record status',
      presence: 'Recorded as',
      expected: 'What comes next',
      entry: 'Record an entry',
      exit: 'Record an exit',
      view: 'Open the record',
      modify: 'Amend the record',
      submitting: 'Recording…',
      another: 'Next band',
    },
    warning: {
      person_blocked: 'This person is blocked.',
      person_archived: 'This record is archived.',
      no_photograph: 'There is no photograph, so you cannot check who this is.',
      name_unavailable: 'The name is not available right now.',
      outside_too_long: 'They went out and had not come back: longer outside than allowed.',
    },
    state: {
      inside: 'Inside',
      outside: 'Outside',
    },
    recordStatus: {
      active: 'Active',
      blocked: 'Blocked',
      archived: 'Archived',
    },
    outcome: {
      accepted: { title: 'Recorded', body: 'They may go through.' },
      override: {
        title: 'Contradicting movement recorded',
        body: 'It is filed as an anomaly with the reason you chose.',
      },
      blocked_override: {
        title: 'Let through with a reason',
        body: 'Who authorised it, and why, is on the record.',
      },
      blocked: {
        title: 'Access blocked',
        body: 'The refusal is on the record. To let them through, give a reason.',
      },
      archived: {
        title: 'Record archived',
        body: 'The record has to be reactivated before any movement can be recorded.',
      },
      override_not_permitted: {
        title: 'This cannot be authorised here',
        body: 'This movement needs a person to authorise it.',
      },
      reason_required: {
        title: 'A reason is missing',
        body: 'Choose why this movement is being recorded.',
      },
      duplicate: {
        title: 'Already recorded',
        body: 'This read repeats a movement that was already on the record.',
      },
      revoked: {
        title: 'Band revoked',
        body: 'This band no longer works. Issue a new one from the record.',
      },
      superseded: {
        title: 'Band replaced',
        body: 'Another band has been issued to this person.',
      },
      blank_unclaimed: {
        title: 'Band not registered',
        body: 'Register this person here to assign them this band.',
      },
      blank_void: {
        title: 'Band withdrawn',
        body: 'This batch has been withdrawn. Do not use it.',
      },
      unknown: {
        title: 'Band not recognised',
        body: 'Check the code, or take this person to registration.',
      },
      malformed: {
        title: 'Wrong code',
        body: 'What was read is not code-shaped: scan the band again.',
      },
      rate_limited: {
        title: 'Too many failed reads',
        body: 'Wait a moment before trying again.',
      },
      index_version_mismatch: {
        title: 'Band from an earlier version',
        body: 'Tell administration: this band cannot be resolved.',
      },
      offline: {
        title: 'No connection',
        body: 'The server was not reached. Try again: nothing will be duplicated.',
      },
      refused: {
        title: 'It could not be recorded',
        body: 'Try again, and tell administration if it keeps happening.',
      },
    },
    duplicate: {
      toast: 'Already recorded {{seconds}} s ago, at {{time}}.',
    },
    anomaly: {
      title: 'Stop',
      contradiction:
        'This person is recorded as {{state}} and the same thing is being recorded again.',
      blocked: 'This person is blocked.',
      instruction: 'Check the photograph and decide before going on.',
      reasonLabel: 'Reason',
      reasonPlaceholder: 'Choose a reason',
      noteLabel: 'Say what happened',
      noteRequired: 'Write an explanation before going on.',
      reasonRequired: 'Choose a reason before going on.',
      acknowledge: 'I understand and want to go on',
      confirm: 'Go on',
      cancel: 'Record nothing',
      mute: 'Silence the alert',
      unmute: 'Restore the sound',
    },
    meal: {
      breakfast: 'Breakfast',
      lunch: 'Lunch',
      dinner: 'Dinner',
      heading: 'Dining room',
      servedAt: 'Served at {{time}}',
      notServed: 'Not yet',
      repeatTitle: 'That meal has already been served',
      repeatBody: 'This person is already recorded as having had {{meal}} today.',
      reasonLabel: 'Reason for serving it again',
      reasonPlaceholder: 'Choose a reason',
      reasonRequired: 'A reason is needed to serve it again.',
      repeatConfirm: 'Serve again',
      retry: 'Try again',
      repeatCancel: 'Do not serve',
    },
    mealReason: {
      meal_spoiled: 'The tray was dropped or spoiled',
      recorded_in_error: 'It was recorded in error',
      staff_authorised: 'Staff authorised it',
    },
    reason: {
      left_via_other_exit: 'Left through another exit',
      entered_via_other_entrance: 'Entered through another entrance',
      gate_unattended: 'Gate was unattended',
      suspected_pass_sharing: 'Pass sharing suspected',
      operator_error: 'Operator error',
      other: 'Other reason',
      system_correction: 'System correction',
    },
    delivery: {
      title: 'Pass for {{name}}',
      revealBody: 'Have the person photograph this screen.',
      reveal: 'Show the code',
      hide: 'Hide the code',
      symbol: 'Pass QR symbol',
      code: 'Code',
      shortRef: 'Reference',
      print: 'Print on A4',
      printing: 'Getting the print ready…',
      printFailed: 'It could not be printed. The pass is still available from the record.',
      bound: 'Pre-printed band assigned',
      boundBody: 'The person is already wearing the band. Nothing needs printing.',
      noPrint: 'A paper copy would be a second pass for one person.',
      calibrateTitle: 'Printer check',
      calibrateBody:
        'Print this page at 100 %, without fitting it to the paper, and measure the square: each side must be {{millimetres}} mm. If it is smaller, the printer is shrinking the page and the codes will not scan reliably.',
      calibrateSquare: 'Reference square of {{millimetres}} mm',
      calibratePrint: 'Print the check',
    },
  },
  errors: {
    detail: {
      subject: '{{subject}}: {{message}}',
      pathSeparator: ' › ',
      atIndex: '{{subject}} [{{index}}]',
      inLanguage: '{{subject}} ({{language}})',
      more: 'And {{count}} more.',
    },
    networkBody: 'Check this terminal’s connection and try again.',
    unexpectedBody: 'If it happens again, tell administration the exact time.',
    sessionInterrupted: 'The terminal locked before this finished. Nothing was saved.',
    retryAfter: 'Try again in {{retryAfterSeconds}} seconds.',
    responseInvalid: 'The server’s answer was not in the expected shape. Nothing has been changed.',
  },
};
